import "core-js/modules/es.array.map.js";
import PropTypes from "prop-types";
import "core-js/modules/es.array.index-of.js";
import "core-js/modules/es.regexp.constructor.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.regexp.sticky.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/es.array.for-each.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Fragment } from 'react';
import { EuiMark } from '../mark';
var highlight = function highlight(searchSubject, searchValue, isStrict, highlightAll, hasScreenReaderHelpText) {
  if (!searchValue) {
    return searchSubject;
  }
  if (!searchSubject) {
    return null;
  }
  if (highlightAll) {
    var chunks = getHightlightWords(searchSubject, searchValue, isStrict);
    return ___EmotionJSX(Fragment, null, chunks.map(function (chunk) {
      var end = chunk.end,
        highlight = chunk.highlight,
        start = chunk.start;
      var value = searchSubject.substring(start, end);
      if (highlight) {
        return ___EmotionJSX(EuiMark, {
          key: start,
          hasScreenReaderHelpText: hasScreenReaderHelpText
        }, value);
      }
      return value;
    }));
  }
  var normalizedSearchSubject = isStrict ? searchSubject : searchSubject.toLowerCase();
  var normalizedSearchValue = isStrict ? searchValue : searchValue.toLowerCase();
  var indexOfMatch = normalizedSearchSubject.indexOf(normalizedSearchValue);
  if (indexOfMatch === -1) {
    return searchSubject;
  }
  var preMatch = searchSubject.substring(0, indexOfMatch);
  var match = searchSubject.substring(indexOfMatch, indexOfMatch + searchValue.length);
  var postMatch = searchSubject.substring(indexOfMatch + searchValue.length);
  return ___EmotionJSX(Fragment, null, preMatch || undefined, ___EmotionJSX(EuiMark, {
    hasScreenReaderHelpText: hasScreenReaderHelpText
  }, match), postMatch || undefined);
};
var getHightlightWords = function getHightlightWords(searchSubject, searchValue, isStrict) {
  var regex = new RegExp(searchValue, isStrict ? 'g' : 'gi');
  var matches = [];
  var match;
  while ((match = regex.exec(searchSubject)) !== null) {
    matches.push({
      start: match.index,
      end: (match.index || 0) + match[0].length
    });
  }
  return fillInChunks(matches, searchSubject.length);
};
var fillInChunks = function fillInChunks(chunksToHighlight, totalLength) {
  var allChunks = [];
  var append = function append(start, end, highlight) {
    if (end - start > 0) {
      allChunks.push({
        start: start,
        end: end,
        highlight: highlight
      });
    }
  };
  if (chunksToHighlight.length === 0) {
    append(0, totalLength, false);
  } else {
    var lastIndex = 0;
    chunksToHighlight.forEach(function (chunk) {
      append(lastIndex, chunk.start, false);
      append(chunk.start, chunk.end, true);
      lastIndex = chunk.end;
    });
    append(lastIndex, totalLength, false);
  }
  return allChunks;
};
export var EuiHighlight = ({
  children,
  className,
  search,
  strict = false,
  highlightAll = false,
  hasScreenReaderHelpText = true,
  ...rest
}) => {
  return <span className={className} {...rest}>
      {highlight(children, search, strict, highlightAll, hasScreenReaderHelpText)}
    </span>;
};
EuiHighlight.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * string to highlight as this component's content
       */
  children: PropTypes.string.isRequired,
  /**
       * What to search for
       */
  search: PropTypes.string.isRequired,
  /**
       * Should the search be strict or not
       */
  strict: PropTypes.bool,
  /**
       * Should highlight all matches
       */
  highlightAll: PropTypes.bool
};
try {
  EuiHighlight.__docgenInfo = {
    tags: {},
    filePath: '/Users/briannahall/Documents/EUI/eui/src/components/highlight/highlight.tsx',
    description: '',
    displayName: 'EuiHighlight',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      children: {
        defaultValue: null,
        description: "string to highlight as this component's content",
        name: 'children',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'eui/src/components/highlight/highlight.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      hasScreenReaderHelpText: {
        defaultValue: {
          value: 'true'
        },
        description: 'Set to `false` to remove the CSS :before and :after\n' + 'screen reader helper text',
        name: 'hasScreenReaderHelpText',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/mark/mark.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      search: {
        defaultValue: null,
        description: 'What to search for',
        name: 'search',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/highlight/highlight.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      strict: {
        defaultValue: {
          value: 'false'
        },
        description: 'Should the search be strict or not',
        name: 'strict',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/highlight/highlight.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      highlightAll: {
        defaultValue: {
          value: 'false'
        },
        description: 'Should highlight all matches',
        name: 'highlightAll',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/highlight/highlight.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}